import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Music, User, LogOut, Shield, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { toast } from "@/hooks/use-toast";

const SPANISH_DOMAINS = [
  "tucancionmomento.com",
  "www.tucancionmomento.com",
];

const getDefaultLang = (): "en" | "es" => {
  // Domain always wins: Spanish domain → Spanish, regardless of localStorage
  if (SPANISH_DOMAINS.includes(window.location.hostname)) return "es";
  const saved = localStorage.getItem("preferred_lang") as "en" | "es" | null;
  if (saved) return saved;
  return "en";
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState<"en" | "es">(getDefaultLang);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { isAdmin } = useAdmin();

  // Load Google Translate script once
  useEffect(() => {
    if (document.getElementById("google-translate-script")) return;

    (window as any).googleTranslateElementInit = () => {
      new (window as any).google.translate.TranslateElement(
        { pageLanguage: "en", autoDisplay: false },
        "google_translate_element"
      );
    };

    const script = document.createElement("script");
    script.id = "google-translate-script";
    script.src =
      "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // Auto-trigger Spanish on first load if domain defaults to es
  useEffect(() => {
    if (currentLang !== "es") return;
    const interval = setInterval(() => {
      const select = document.querySelector<HTMLSelectElement>(".goog-te-combo");
      if (select) {
        select.value = "es";
        select.dispatchEvent(new Event("change"));
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLanguage = useCallback(() => {
    const newLang = currentLang === "en" ? "es" : "en";
    setCurrentLang(newLang);
    localStorage.setItem("preferred_lang", newLang);

    // Trigger Google Translate
    const select = document.querySelector<HTMLSelectElement>(
      ".goog-te-combo"
    );
    if (select) {
      select.value = newLang === "es" ? "es" : "en";
      select.dispatchEvent(new Event("change"));
    }
  }, [currentLang]);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    } else {
      navigate("/");
    }
  };

  const handleHashClick = (hash: string) => {
    setIsMenuOpen(false);
    const id = hash.replace("#", "");
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "#listen", label: "Samples" },
    { href: "#pricing", label: "Pricing" },
    { href: "/blog", label: "Blog" },
    { href: "/faq", label: "FAQ" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass pt-5">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="/images/YourMomentSonglogo_transparent_BG.png"
              alt="Your Moment Song"
              className="h-[7.1rem] md:h-[8.1rem] w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Main navigation" className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.href.startsWith("#") ? (
                <button
                  key={link.href}
                  onClick={() => handleHashClick(link.href)}
                  className="font-medium transition-colors duration-200 text-muted-foreground hover:text-foreground"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-medium transition-colors duration-200 ${
                    isActive(link.href)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="notranslate flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 px-1.5 py-0.5 rounded-md border border-border hover:border-primary/30"
              aria-label={currentLang === "en" ? "Español — switch language" : "English — switch language"}
            >
              <Globe className="w-3.5 h-3.5" aria-hidden="true" />
              {currentLang === "en" ? "Español" : "English"}
            </button>
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {loading ? (
              <div className="w-24 h-10 shimmer rounded-lg" />
            ) : user ? (
              <>
                {isAdmin && (
                  <Button variant="ghost" asChild>
                    <Link to="/admin">
                      <Shield className="w-4 h-4 mr-2" aria-hidden="true" />
                      Admin
                    </Link>
                  </Button>
                )}
                <Button variant="ghost" asChild>
                  <Link to="/dashboard">
                    <User className="w-4 h-4 mr-2" aria-hidden="true" />
                    Dashboard
                  </Link>
                </Button>
                <Button variant="outline" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/login">Sign In</Link>
                </Button>
                <Button variant="hero" asChild>
                  <Link to="/signup">Get Started</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Auth + Menu */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Language Toggle - always visible */}
            <button
              onClick={toggleLanguage}
              className="notranslate flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 px-1.5 py-0.5 rounded-md border border-border"
              aria-label={currentLang === "en" ? "Español — switch language" : "English — switch language"}
            >
              <Globe className="w-3.5 h-3.5" aria-hidden="true" />
              {currentLang === "en" ? "Español" : "English"}
            </button>
            {!loading && !user && (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/login">Sign In</Link>
              </Button>
            )}
            <button
              className="p-2 text-foreground"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-card border-t border-border"
          >
            <nav aria-label="Mobile navigation" className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navLinks.map((link) =>
                link.href.startsWith("#") ? (
                  <button
                    key={link.href}
                    onClick={() => handleHashClick(link.href)}
                    className="font-medium py-2 text-muted-foreground text-left"
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`font-medium py-2 ${
                      isActive(link.href)
                        ? "text-primary"
                        : "text-muted-foreground"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
              {/* Mobile Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 font-medium py-2 text-muted-foreground"
              >
                <Globe className="w-4 h-4" aria-hidden="true" />
                {currentLang === "en" ? "Español" : "English"}
              </button>
              <div className="flex flex-col gap-2 pt-4 border-t border-border">
                {user ? (
                  <>
                    {isAdmin && (
                      <Button variant="outline" asChild>
                        <Link to="/admin" onClick={() => setIsMenuOpen(false)}>
                          Admin Panel
                        </Link>
                      </Button>
                    )}
                    <Button variant="outline" asChild>
                      <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                        Dashboard
                      </Link>
                    </Button>
                    <Button variant="ghost" onClick={handleLogout}>
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" asChild>
                      <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                        Sign In
                      </Link>
                    </Button>
                    <Button variant="hero" asChild>
                      <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                        Get Started
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Hidden Google Translate element */}
      <div id="google_translate_element" className="hidden" />
    </header>
  );
};

export default Header;
